/* src/ui/confirm.ts — "are you sure" for the irreversible things (TASKS.md 4.4).
 *
 * Buying a building and demolishing one both move money in ways the player cannot undo, and both
 * are one click away from a camera drag. So they go through here.
 *
 * A promise, not a callback, because every call site is already inside an async flow: `if (await
 * confirm(...))` reads as the decision it is, where a callback would fork the flow in two.
 *
 * The dialog owns focus while it is up. Escape cancels, Enter confirms, and focus returns to
 * whatever had it — without that, dismissing a modal leaves the keyboard pointing at nothing.
 */

import { el, text } from './dom.ts';
import { showScrim } from './scrim.ts';

export interface ConfirmOptions {
  readonly title: string;
  readonly body: string;
  /** Label for the affirmative button. Say what will happen — "Buy for $150", not "OK". */
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  /** Styles the confirm button as destructive. For demolition and selling, not for buying. */
  readonly destructive?: boolean;
}

export function confirmDialog(root: HTMLElement, opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const previousFocus = document.activeElement;
    let settled = false;

    const finish = (value: boolean): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('keydown', onKey, true);
      dialog.remove();
      scrim.release();
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
      resolve(value);
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        finish(true);
      }
    };

    const confirmBtn = el(
      'button',
      {
        class: `gf-confirm-yes${opts.destructive === true ? ' is-destructive' : ''}`,
        attrs: { type: 'button' },
        on: { click: () => finish(true) },
      },
      opts.confirmLabel,
    );

    const dialog = el(
      'div',
      { class: 'gf-confirm', attrs: { role: 'dialog', 'aria-modal': 'true' } },
      el('h3', { class: 'gf-confirm-title' }, opts.title),
      el('p', { class: 'gf-confirm-body' }, text(opts.body)),
      el(
        'div',
        { class: 'gf-confirm-actions' },
        el(
          'button',
          {
            class: 'gf-confirm-no',
            attrs: { type: 'button' },
            on: { click: () => finish(false) },
          },
          opts.cancelLabel ?? 'Cancel',
        ),
        confirmBtn,
      ),
    );

    // A click on the backdrop cancels — safe here because the affirmative action always needs a
    // deliberate press, so the worst a stray click can do is close the dialog.
    const scrim = showScrim(root, () => finish(false));
    root.appendChild(dialog);
    window.addEventListener('keydown', onKey, true);
    confirmBtn.focus();
  });
}
